import {
  searchProvidersWithMeta,
  countProvidersWithFilters,
  parseAiQuery,
  findProvidersByIdentifier,
  parseLocationToCityState,
  SPECIALTIES,
  CITIES,
  type Provider,
  type SearchMeta,
} from "@/data/heallexa";

// Lifted out of routes/search.tsx so /providers/$id and /providers/$state/$city
// can run the exact same AI-query parsing, identifier lookup, and loose-city
// fallback that /search already has, instead of a thinner per-route fetch.

export const SEARCH_RESULT_LIMIT = 20;

const US_STATE_NAME_TO_CODE = new Map<string, string>([
  ["alabama","AL"],["alaska","AK"],["arizona","AZ"],["arkansas","AR"],
  ["california","CA"],["colorado","CO"],["connecticut","CT"],["delaware","DE"],
  ["florida","FL"],["georgia","GA"],["hawaii","HI"],["idaho","ID"],
  ["illinois","IL"],["indiana","IN"],["iowa","IA"],["kansas","KS"],
  ["kentucky","KY"],["louisiana","LA"],["maine","ME"],["maryland","MD"],
  ["massachusetts","MA"],["michigan","MI"],["minnesota","MN"],["mississippi","MS"],
  ["missouri","MO"],["montana","MT"],["nebraska","NE"],["nevada","NV"],
  ["new hampshire","NH"],["new jersey","NJ"],["new mexico","NM"],["new york","NY"],
  ["north carolina","NC"],["north dakota","ND"],["ohio","OH"],["oklahoma","OK"],
  ["oregon","OR"],["pennsylvania","PA"],["rhode island","RI"],["south carolina","SC"],
  ["south dakota","SD"],["tennessee","TN"],["texas","TX"],["utah","UT"],
  ["vermont","VT"],["virginia","VA"],["washington","WA"],["west virginia","WV"],
  ["wisconsin","WI"],["wyoming","WY"],["district of columbia","DC"],
]);
const US_STATE_CODES = new Set<string>(Array.from(US_STATE_NAME_TO_CODE.values()));

function emptyMeta(radius: number | undefined): SearchMeta {
  return {
    requestedRadiusMiles: radius && radius > 0 ? Math.min(radius, 50) : 0,
    usedRadius: false,
    centroid: null,
  };
}

function inferStateFromText(text: string | undefined): string | undefined {
  if (!text) return undefined;
  const m = text.match(/\b([A-Za-z]{2})\b\s*$/);
  if (m) {
    const code = m[1].toUpperCase();
    if (US_STATE_CODES.has(code)) return code;
  }
  const lower = ` ${text.toLowerCase()} `;
  for (const [name, code] of US_STATE_NAME_TO_CODE) {
    if (lower.includes(` ${name} `)) return code;
  }
  return undefined;
}

function extractLocationFromSearchText(text: string): string | undefined {
  const raw = text.trim().replace(/\s+/g, " ");
  const explicit = raw.match(/\b(?:in|near|around)\s+([^?]+)$/i)?.[1];
  const candidate = (explicit || "")
    .replace(/\b(?:who|that)?\s*(?:accepts?|take|takes|with|for|open|available)\b.*$/i, "")
    .replace(/[.!?]+$/g, "")
    .trim();
  if (candidate) return candidate;
  const knownCity = CITIES.find((c) => raw.toLowerCase().includes(c.split(",")[0].toLowerCase()));
  return knownCity || undefined;
}

function extractSpecialtyFromSearchText(text: string): string | undefined {
  const lower = text.toLowerCase();
  return SPECIALTIES.find((s) => {
    const label = s.toLowerCase();
    const primary = label.split("/")[0].trim();
    const singular = primary.replace(/s\b/, "");
    return lower.includes(label) || lower.includes(primary) || lower.includes(singular);
  });
}

// Query strings may arrive with `+` representing space (form-encoded URLs from
// external links, share cards, IndexNow rewrites). decodeURIComponent does NOT
// translate `+` → space, so we must clean text params explicitly before they
// reach the database. Without this, `location=Fort+Worth%2C+TX` becomes
// "Fort+Worth, TX" and `_city = 'Fort+Worth'` matches zero providers.
export function cleanQueryText(v: unknown): string | undefined {
  if (typeof v !== "string") return undefined;
  let s = v;
  // Some upstream callers double-encode; try one defensive decode pass.
  if (/%[0-9A-Fa-f]{2}/.test(s)) {
    try { s = decodeURIComponent(s); } catch { /* keep original */ }
  }
  s = s.replace(/\+/g, " ").replace(/\s+/g, " ").trim();
  return s || undefined;
}

export type SearchLoaderDeps = {
  specialty?: string;
  location?: string;
  insurance?: string;
  q?: string;
  radius?: number;
  page?: number;
  // Device geolocation ("near me"): distance-scoped results + per-provider distance.
  lat?: number;
  lng?: number;
};

export type SearchLoaderResult = {
  results: Provider[];
  page: number;
  hasMore: boolean;
  total: number;
  totalCapped: boolean;
  filters: { specialty: string; location: string; insurance: string };
  aiQuery: string | null;
  ai: null;
  meta: SearchMeta;
};

export async function runProviderSearch(deps: SearchLoaderDeps): Promise<SearchLoaderResult> {
  const page = Math.max(1, Number(deps.page) || 1);
  const offset = (page - 1) * SEARCH_RESULT_LIMIT;
  // Fetch PAGE_SIZE + 1 to detect whether a "next" page exists without
  // running an expensive COUNT(*) over the 6M-row providers table.
  const fetchLimit = SEARCH_RESULT_LIMIT + 1;

  if (deps.q) {
    const parsedLocal = parseAiQuery(deps.q);
    const explicitLocation = extractLocationFromSearchText(deps.q) || parsedLocal.location || deps.location;
    const explicitSpecialty = extractSpecialtyFromSearchText(deps.q) || parsedLocal.specialty;
    const localParsedLoc = parseLocationToCityState(explicitLocation);
    const inferredState =
      localParsedLoc.state ||
      inferStateFromText(deps.q) ||
      parseLocationToCityState(deps.location).state;

    const baseCity =
      (deps.radius && deps.location)
        ? deps.location
        : explicitLocation;
    const baseParsed = parseLocationToCityState(baseCity);
    const composedLocation = baseParsed.city
      ? (baseParsed.state
          ? `${baseParsed.city}, ${baseParsed.state}`
          : (inferredState ? `${baseParsed.city}, ${inferredState}` : baseParsed.city))
      : baseCity;

    const tokens = Array.from(
      new Set(
        (deps.q.match(/\b[A-Za-z0-9-]{4,20}\b/g) || []).filter((tok) => {
          if (/^\d{10}$/.test(tok)) return true;
          if (/^[A-Za-z]{0,4}\d{3,}[A-Za-z0-9-]*$/.test(tok)) return true;
          return false;
        }),
      ),
    ).slice(0, 3);

    const hasLocalFilters = !!(explicitSpecialty || explicitLocation || parsedLocal.insurance);
    const structuredQ = hasLocalFilters ? undefined : deps.q;

    const baseArgs = {
      q: structuredQ,
      specialty: explicitSpecialty || undefined,
      location: composedLocation,
      state: inferredState,
      insurance: parsedLocal.insurance || undefined,
      radiusMiles: deps.radius || 0,
      lat: deps.lat,
      lng: deps.lng,
    };

    const [primary, totalRes, ...idResults] = await Promise.all([
      searchProvidersWithMeta({
        ...baseArgs,
        limit: fetchLimit,
        offset,
      }).catch(() => ({ results: [] as Provider[], meta: emptyMeta(deps.radius) })),
      countProvidersWithFilters(baseArgs).catch(() => ({ count: 0, capped: false })),
      // Identifier lookup only on page 1 (it's a small global lookup, not paged).
      ...(page === 1
        ? tokens.map((t) => findProvidersByIdentifier(t, 8).catch(() => [] as Provider[]))
        : []),
    ]);

    const hasMore = primary.results.length > SEARCH_RESULT_LIMIT;
    const pageRows = primary.results.slice(0, SEARCH_RESULT_LIMIT);

    const seen = new Set<string>();
    const merged: Provider[] = [];
    for (const list of [...idResults, pageRows]) {
      for (const p of list) {
        if (!seen.has(p.id)) { seen.add(p.id); merged.push(p); }
      }
    }
    return {
      results: merged.slice(0, SEARCH_RESULT_LIMIT),
      page,
      hasMore,
      total: totalRes.count,
      totalCapped: totalRes.capped,
      filters: {
        specialty: explicitSpecialty || "",
        location: composedLocation || "",
        insurance: parsedLocal.insurance || "",
      },
      aiQuery: deps.q,
      ai: null,
      meta: primary.meta,
    };
  }

  const filters = {
    specialty: deps.specialty || "",
    location: deps.location || "",
    insurance: deps.insurance || "",
  };
  const depsParsed = parseLocationToCityState(deps.location);
  const baseArgs = {
    ...filters,
    state: depsParsed.state,
    radiusMiles: deps.radius || 0,
    lat: deps.lat,
    lng: deps.lng,
  };
  const [resultsRes, countRes] = await Promise.all([
    searchProvidersWithMeta({ ...baseArgs, limit: fetchLimit, offset })
      .catch((e) => {
        console.error("search loader: searchProvidersWithMeta failed", {
          ...filters, radius: deps.radius || 0, page,
          error: e instanceof Error ? { message: e.message, stack: e.stack } : e,
        });
        return { results: [] as Provider[], meta: emptyMeta(deps.radius) };
      }),
    countProvidersWithFilters(baseArgs).catch(() => ({ count: 0, capped: false })),
  ]);
  const { results, meta } = resultsRes;

  let hasMore = results.length > SEARCH_RESULT_LIMIT;
  let finalResults = results.slice(0, SEARCH_RESULT_LIMIT);
  let finalMeta = meta;
  let total = countRes.count;
  let totalCapped = countRes.capped;

  // Fallback: a geo-scoped ("near me") search that comes up empty is most
  // likely a sparse/rural area, not zero doctors — widen the radius once
  // before giving up. Real medical search, so a dead-end empty state is
  // worse than a wider result set. 50mi is the system-wide ceiling — see
  // the `_radius_miles` clamp in search-providers.functions.ts — so there's
  // nothing left to widen to beyond it.
  const RADIUS_CEILING_MILES = 50;
  if (page === 1 && finalResults.length === 0 && deps.lat != null && deps.lng != null &&
      (deps.radius || 0) > 0 && (deps.radius || 0) < RADIUS_CEILING_MILES) {
    try {
      const wideArgs = { ...baseArgs, radiusMiles: RADIUS_CEILING_MILES };
      const [wide, wideCount] = await Promise.all([
        searchProvidersWithMeta({ ...wideArgs, limit: fetchLimit, offset: 0 }),
        countProvidersWithFilters(wideArgs).catch(() => ({ count: 0, capped: false })),
      ]);
      if (wide.results.length > 0) {
        hasMore = wide.results.length > SEARCH_RESULT_LIMIT;
        finalResults = wide.results.slice(0, SEARCH_RESULT_LIMIT);
        finalMeta = wide.meta;
        total = wideCount.count;
        totalCapped = wideCount.capped;
      }
    } catch { /* keep empty, fallbacks below still run */ }
  }

  // Final fallback: a geo-only search (no typed location, so the city
  // fallback below can't help) that's still empty even at the radius
  // ceiling is a true desert — drop the distance scoping entirely rather
  // than dead-end on "No providers found" for a real medical search.
  if (page === 1 && finalResults.length === 0 && deps.lat != null && deps.lng != null &&
      !deps.location) {
    try {
      const nationwideArgs = { ...filters, state: depsParsed.state, radiusMiles: 0 };
      const [nationwide, nationwideCount] = await Promise.all([
        searchProvidersWithMeta({ ...nationwideArgs, limit: fetchLimit, offset: 0 }),
        countProvidersWithFilters(nationwideArgs).catch(() => ({ count: 0, capped: false })),
      ]);
      if (nationwide.results.length > 0) {
        hasMore = nationwide.results.length > SEARCH_RESULT_LIMIT;
        finalResults = nationwide.results.slice(0, SEARCH_RESULT_LIMIT);
        finalMeta = nationwide.meta;
        total = nationwideCount.count;
        totalCapped = nationwideCount.capped;
      }
    } catch { /* keep empty, loose city fallback below still runs */ }
  }

  // Fallback: only on page 1, if exact city/state returned nothing, retry
  // with a looser city-only query so users never land on a blank slate.
  if (page === 1 && finalResults.length === 0 && (depsParsed.city || deps.location)) {
    try {
      const looseArgs = {
        specialty: filters.specialty,
        location: depsParsed.city || deps.location,
        insurance: filters.insurance,
        radiusMiles: 0,
      };
      const [loose, looseCount] = await Promise.all([
        searchProvidersWithMeta({ ...looseArgs, limit: fetchLimit, offset: 0 }),
        countProvidersWithFilters(looseArgs).catch(() => ({ count: 0, capped: false })),
      ]);
      if (loose.results.length > 0) {
        hasMore = loose.results.length > SEARCH_RESULT_LIMIT;
        finalResults = loose.results.slice(0, SEARCH_RESULT_LIMIT);
        finalMeta = loose.meta;
        total = looseCount.count;
        totalCapped = looseCount.capped;
      }
    } catch { /* keep empty */ }
  }
  return { results: finalResults, page, hasMore, total, totalCapped, filters, aiQuery: null, ai: null, meta: finalMeta };
}
